import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import {
  AlertTriangle, Zap, Clock, Send, CheckCircle2,
  Building2, TrendingUp, History
} from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

function AlertCard({ alert, onRelance, relancing }) {
  const isCritical = alert.severity === "critical";
  return (
    <div data-testid={`alert-${alert.invoice_id?.slice(0, 8)}`}
      className={`bg-white border rounded-xl p-5 transition-all hover:shadow-lg ${isCritical ? "border-red-200 shadow-red-100/50" : "border-amber-200 shadow-amber-100/50"}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCritical ? "bg-red-50" : "bg-amber-50"}`}>
            <AlertTriangle className={`w-5 h-5 ${isCritical ? "text-red-500" : "text-amber-500"}`} />
          </div>
          <div>
            <h3 className="text-slate-800 font-semibold">{alert.insurer}</h3>
            <p className="text-xs text-slate-500">{alert.invoice_number} - {alert.pharmacy_name}</p>
          </div>
        </div>
        <span className={`text-xs font-bold px-3 py-1 rounded-full ${isCritical ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
          {isCritical ? "CRITIQUE" : "ATTENTION"}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div><p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">Montant</p><p className="text-slate-800 font-bold">{fmt(alert.amount)} <span className="text-xs font-normal text-slate-400">FCFA</span></p></div>
        <div><p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">En attente</p><p className={`font-bold ${isCritical ? "text-red-600" : "text-amber-600"}`}>{alert.days_waiting} <span className="text-xs font-normal">jours</span></p></div>
        <div><p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">Delai moyen</p><p className="text-slate-600 font-medium">{alert.avg_delay} <span className="text-xs font-normal text-slate-400">jours</span></p></div>
        <div><p className="text-[11px] text-slate-400 uppercase tracking-wider mb-0.5">Depassement</p><p className="text-red-600 font-bold">+{alert.excess_days} <span className="text-xs font-normal">jours</span></p></div>
      </div>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[11px] text-slate-400">Prediction IA</span>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
            alert.prediction.risk_level === "CRITICAL" ? "bg-red-100 text-red-700" : alert.prediction.risk_level === "HIGH" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"
          }`}>Risque {alert.prediction.risk_level === "CRITICAL" ? "critique" : alert.prediction.risk_level === "HIGH" ? "eleve" : "moyen"}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all ${isCritical ? "bg-red-500" : "bg-amber-500"}`}
            style={{ width: `${Math.min(100, (alert.days_waiting / (alert.avg_delay * 2)) * 100)}%` }} />
        </div>
        {alert.prediction.estimated_remaining > 0 && (
          <p className="text-[11px] text-slate-400 mt-1">Estimation: encore ~{alert.prediction.estimated_remaining} jours avant paiement</p>
        )}
      </div>
      <Button data-testid={`relance-btn-${alert.invoice_id?.slice(0, 8)}`} onClick={() => onRelance(alert.invoice_id)} disabled={relancing === alert.invoice_id}
        className={`w-full font-semibold ${isCritical ? "bg-red-600 hover:bg-red-700 text-white" : "bg-amber-500 hover:bg-amber-600 text-white"}`}>
        <Send className={`w-4 h-4 mr-2 ${relancing === alert.invoice_id ? "animate-spin" : ""}`} />
        {relancing === alert.invoice_id ? "Envoi en cours..." : "Relancer l'assureur"}
      </Button>
    </div>
  );
}

export default function SmartAlertsPage() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [relancing, setRelancing] = useState(null);
  const [tab, setTab] = useState("alerts");

  const load = useCallback(async () => {
    try {
      const [alertRes, histRes] = await Promise.all([
        axios.get(`${API}/smart-alerts/check`, { headers: getHeaders() }),
        axios.get(`${API}/smart-alerts/history`, { headers: getHeaders() }),
      ]);
      setData(alertRes.data);
      setHistory(histRes.data.history || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRelance = async (invoiceId) => {
    setRelancing(invoiceId);
    try {
      const res = await axios.post(`${API}/smart-alerts/relance/${invoiceId}`, {}, { headers: getHeaders() });
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error("Erreur lors de la relance"); }
    finally { setRelancing(null); }
  };

  const summary = data?.summary || {};
  const alerts = data?.alerts || [];
  const insurerStats = data?.insurer_stats || [];

  return (
    <LogLayout title="Alertes Intelligentes" subtitle="Suivi proactif des retards et relances automatiques">
      <div data-testid="smart-alerts-page" className="space-y-6">
        <div className="flex gap-2">
          <Button data-testid="tab-alerts" onClick={() => setTab("alerts")} className={tab === "alerts" ? "bg-sky-600 text-white hover:bg-sky-700" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}>
            <Zap className="w-4 h-4 mr-1.5" /> Alertes actives
          </Button>
          <Button data-testid="tab-insurers" onClick={() => setTab("insurers")} className={tab === "insurers" ? "bg-sky-600 text-white hover:bg-sky-700" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}>
            <Building2 className="w-4 h-4 mr-1.5" /> Assureurs
          </Button>
          <Button data-testid="tab-history" onClick={() => setTab("history")} className={tab === "history" ? "bg-sky-600 text-white hover:bg-sky-700" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}>
            <History className="w-4 h-4 mr-1.5" /> Historique relances
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {tab === "alerts" && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { icon: AlertTriangle, color: "amber", value: summary.total_alerts || 0, label: "Alertes retard", border: "border-amber-200" },
                    { icon: Zap, color: "red", value: summary.critical || 0, label: "Critiques", border: "border-red-200" },
                    { icon: TrendingUp, color: "sky", value: fmt(summary.total_amount_at_risk || 0), label: "FCFA a risque", border: "border-sky-200" },
                    { icon: Building2, color: "violet", value: summary.creance_alerts_count || 0, label: "Alertes creances", border: "border-violet-200" },
                  ].map((kpi, i) => (
                    <div key={kpi.label} className={`bg-white border ${kpi.border} rounded-xl p-4 shadow-sm`}>
                      <kpi.icon className={`w-4 h-4 text-${kpi.color}-500 mb-2`} />
                      <p className={`text-2xl font-bold text-${kpi.color}-600`}>{kpi.value}</p>
                      <p className="text-xs text-slate-500">{kpi.label}</p>
                    </div>
                  ))}
                </div>

                {/* Créances threshold alerts */}
                {(data?.creance_alerts || []).length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Building2 className="w-4 h-4 text-violet-500" /> Depassement plafond creances</h3>
                    {data.creance_alerts.map((ca, i) => (
                      <div key={ca.insurer || i} className={`bg-white border rounded-xl p-4 shadow-sm ${ca.severity === "critical" ? "border-red-200" : "border-amber-200"}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className={`w-5 h-5 ${ca.severity === "critical" ? "text-red-500" : "text-amber-500"}`} />
                            <span className="text-slate-800 font-semibold">{ca.insurer}</span>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${ca.severity === "critical" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                            {ca.usage_percent}% du plafond
                          </span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-2">
                          <div className={`h-full rounded-full ${ca.severity === "critical" ? "bg-red-500" : "bg-amber-500"}`}
                            style={{ width: `${Math.min(ca.usage_percent, 100)}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-slate-500">
                          <span>Total facturé: <strong className="text-slate-700">{fmt(ca.total_remaining + (ca.total_paid || 0))} FCFA</strong></span>
                          <span>Restant: <strong className="text-red-600">{fmt(ca.total_remaining)} FCFA</strong></span>
                          <span>Plafond: <strong className="text-slate-700">{fmt(ca.ceiling_amount)} FCFA</strong></span>
                          <span>{ca.invoice_count} facture(s)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {alerts.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{alerts.map(alert => <AlertCard key={alert.id} alert={alert} onRelance={handleRelance} relancing={relancing} />)}</div>
                ) : (
                  <div className="text-center py-16 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                    <p className="text-slate-800 font-semibold">Aucune alerte de retard active</p>
                    <p className="text-sm text-slate-500 mt-1">Tous les assureurs respectent leurs delais de traitement</p>
                  </div>
                )}
              </>
            )}

            {tab === "insurers" && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-sky-500" /> Delais moyens par assureur
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-slate-400 border-b border-slate-200">
                      <th className="text-left py-3 px-3">Assureur</th><th className="text-right py-3 px-3">Factures total</th><th className="text-right py-3 px-3">En attente</th><th className="text-right py-3 px-3">Delai moyen</th><th className="text-center py-3 px-3">Etat</th>
                    </tr></thead>
                    <tbody>{insurerStats.map((ins, i) => (
                      <tr key={ins.name || i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-3 text-slate-800 font-medium">{ins.name}</td>
                        <td className="py-3 px-3 text-right text-slate-500">{ins.total_invoices}</td>
                        <td className="py-3 px-3 text-right text-amber-600 font-semibold">{ins.pending_count}</td>
                        <td className="py-3 px-3 text-right text-slate-800 font-bold">{ins.avg_delay_days}j</td>
                        <td className="py-3 px-3 text-center">
                          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                            ins.avg_delay_days <= 30 ? "bg-emerald-100 text-emerald-700" : ins.avg_delay_days <= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"
                          }`}>{ins.avg_delay_days <= 30 ? "Normal" : ins.avg_delay_days <= 60 ? "Lent" : "Critique"}</span>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "history" && (
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                  <History className="w-4 h-4 text-sky-500" /> Historique des relances
                </h3>
                {history.length > 0 ? (
                  <div className="space-y-3">{history.map((h, i) => (
                    <div key={h.id || h.invoice_number || i} className="flex items-center gap-4 p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center"><Send className="w-4 h-4 text-sky-600" /></div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-800 font-medium">{h.insurer} - {h.invoice_number}</p>
                        <p className="text-xs text-slate-500">{h.pharmacy_name} - {fmt(h.amount)} FCFA - {h.days_waiting}j d'attente</p>
                      </div>
                      <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleDateString("fr-FR")}</span>
                    </div>
                  ))}</div>
                ) : (
                  <p className="text-center text-slate-400 py-8">Aucune relance effectuee</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </LogLayout>
  );
}
